/**
 * The /deepswe page: benchmark score against the cost of getting useful work
 * done, one line per model.
 *
 * Built to server/static/deepswe.min.js with `edwh build-js` (bun). Chart.js is
 * loaded from a CDN as a UMD global by deepswe.html.
 */

/*
 * Chart.js, loaded as a UMD global by deepswe.html. The camelCase below is not
 * ours: these are the exact keys Chart.js reads and the exact methods it calls,
 * so renaming them to snake_case would break at runtime. Our own code below
 * uses snake_case.
 */
declare const Chart: new (canvas: HTMLCanvasElement, config: unknown) => ChartInstance;

interface ChartArea {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

interface ChartPoint {
    x: number;
    y: number;
    effort: string;
    model: string;
}

interface ChartDataset {
    label: string;
    hidden?: boolean;
    data: ChartPoint[];
    borderColor: string;
    backgroundColor: string;
    borderWidth: number;
    pointRadius: number;
    pointHoverRadius: number;
    showLine: boolean;
    tension: number;
}

interface DatasetMeta {
    hidden: boolean;
    data: { x: number; y: number }[];
}

interface ChartInstance {
    ctx: CanvasRenderingContext2D;
    chartArea: ChartArea;
    scales: Record<string, { min: number; max: number }>;
    data: { datasets: ChartDataset[] };
    options: unknown;
    getDatasetMeta(index: number): DatasetMeta;
    update(): void;
}

interface Config {
    // Served by Pretpakket, which keeps the remote payload in a TTL cache.
    data_url: string;
}

interface Row {
    model: string;
    display: string;
    lab: string;
    effort: string;
    score: number;
    cost: number;
}

interface Hsl {
    h: number;
    s: number;
    l: number;
}

type RawRow = Record<string, unknown>;

/* ------------------------------------------------------------------ *
 * Configuration
 * ------------------------------------------------------------------ */

function read_config(): Config {
    const block = document.getElementById("deepswe-config");
    if (!block) {
        throw new Error("No #deepswe-config in the page.");
    }

    return JSON.parse(block.textContent || "{}") as Config;
}

const CONFIG = read_config();

// Field names in the source JSON. Change here if the schema moves.
const FIELDS = {
    model: "model",
    effort: "reasoning_effort",
    score: "pass_at_1",
    cost: "mean_cost_usd", // "avg cost per task"; median_cost_usd also available
};

const AXIS = {
    score_max: 80, // percent
    score_step: 10,
    default_cutoff: 14, // dollars; matches the published chart's clip point
    default_floor: 0, // percent; the whole range, until you ask for less of it
};

/** Where the axes are clipped; lives in the URL, so a view is linkable. */
interface View {
    cutoff: number;
    floor: number;
}

/* Lab attribution: first matching prefix wins. */
const LAB_RULES: [string, string][] = [
    ["claude", "Anthropic"],
    ["gpt", "OpenAI"],
    ["gemini", "Google"],
    ["grok", "xAI"],
    ["kimi", "Moonshot"],
    ["deepseek", "DeepSeek"],
    ["qwen", "Alibaba"],
    ["muse", "Meta"],
    ["glm", "Z.AI"],
];

/* The API publishes no palette. Ours is one seed hue per lab; the per-model
   shade is derived at runtime from the model id (see shade_for), so a new
   model gets its own shade without disturbing any existing line. */
const LAB_HUES: Record<string, string> = {
    Anthropic: "#d85a30",
    OpenAI: "#0f6e56",
    Google: "#2a78d6",
    xAI: "#4a3aa7",
    Moonshot: "#e34948",
    DeepSeek: "#8a4fc4",
    Alibaba: "#185fa5",
    Meta: "#1baf7a",
    "Z.AI": "#0f766e",
};

/* Bounds for the derived shades, picked for legibility on each background.
   Lightness does most of the separating; a little hue jitter keeps same-lab
   lines from reading as one thick band. */
const SHADE = {
    hue_jitter_deg: 14,
    light: { min_l: 30, max_l: 62 },
    dark: { min_l: 48, max_l: 78 },
    // Two shades in one lab are "too close" below both thresholds.
    min_delta_l: 8,
    min_delta_h: 8,
};

/* Display-name exceptions only. Everything else is derived by prettify_id,
   which dots hyphenated version segments (claude-opus-4-8 -> claude-opus-4.8).
   Add an entry only where that rule gets it wrong, e.g.
   "gemini-3-1-pro-preview": "gemini-3.1-pro" */
const DISPLAY_OVERRIDES: Record<string, string> = {};

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

function el<T extends HTMLElement = HTMLElement>(id: string): T {
    const node = document.getElementById(id);
    if (!node) {
        throw new Error(`No #${id} in the page.`);
    }

    return node as T;
}

function message_of(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

/* ------------------------------------------------------------------ *
 * Data loading and normalisation
 * ------------------------------------------------------------------ */

/** Pull the row array out of the payload without assuming the wrapper key. */
function extract_rows(payload: unknown): RawRow[] {
    if (Array.isArray(payload)) {
        return payload as RawRow[];
    }

    const wrapper = (payload ?? {}) as Record<string, unknown>;
    for (const key of ["rows", "results", "leaderboard", "entries", "data"]) {
        if (Array.isArray(wrapper[key])) {
            return wrapper[key] as RawRow[];
        }
    }

    const first_array = Object.values(wrapper).find(Array.isArray);
    if (first_array) {
        return first_array as RawRow[];
    }

    throw new Error("No array of rows found in the payload.");
}

/* ---- Identity: lab, display name, colour ---- */

/** Prefer a provider field if the API ever ships one; fall back to prefixes. */
function lab_for(row: RawRow): string {
    const explicit = row.provider ?? row.lab ?? row.org;
    if (explicit) {
        return String(explicit);
    }

    const id = String(row[FIELDS.model] ?? "");
    return LAB_RULES.find(([prefix]) => id.startsWith(prefix))?.[1] ?? "Other";
}

/** claude-opus-4-8 -> claude-opus-4.8; leaves non-version hyphens alone. */
function prettify_id(id: string): string {
    return String(id).replace(/(\d)-(?=\d)/g, "$1.");
}

function display_name(id: string): string {
    return DISPLAY_OVERRIDES[id] ?? prettify_id(id);
}

/* FNV-1a, normalised to [0,1). Stable across sessions and browsers. */
function hash01(text: string): number {
    let hash = 2166136261;
    for (let index = 0; index < text.length; index++) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0) / 4294967296;
}

function hex_to_hsl(hex: string): Hsl {
    const packed = parseInt(hex.slice(1), 16);
    const r = ((packed >> 16) & 255) / 255;
    const g = ((packed >> 8) & 255) / 255;
    const b = (packed & 255) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    const lightness = (max + min) / 2;

    if (!delta) {
        return { h: 0, s: 0, l: lightness * 100 };
    }

    const saturation = delta / (1 - Math.abs(2 * lightness - 1));

    let hue: number;
    if (max === r) {
        hue = ((g - b) / delta) % 6;
    } else if (max === g) {
        hue = (b - r) / delta + 2;
    } else {
        hue = (r - g) / delta + 4;
    }

    return { h: (hue * 60 + 360) % 360, s: saturation * 100, l: lightness * 100 };
}

function prefers_dark(): boolean {
    return matchMedia("(prefers-color-scheme: dark)").matches;
}

/** Unknown labs still get a stable hue rather than falling back to grey. */
function lab_seed(lab: string): Hsl {
    const hex = LAB_HUES[lab];
    return hex ? hex_to_hsl(hex) : { h: hash01(lab) * 360, s: 48, l: 45 };
}

/**
 * Deterministic shade for one model inside its lab's hue.
 *
 * Derived from the model id alone, so adding a model never recolours the
 * others. Trade-off: two ids in a lab can hash close together, which
 * build_palette resolves by re-hashing with a salt.
 */
function shade_for(model_id: string, lab: string, salt: number): Hsl {
    const seed = lab_seed(lab);
    const band = prefers_dark() ? SHADE.dark : SHADE.light;
    const key = salt ? `${model_id}~${salt}` : model_id;
    const jitter = (hash01(key) - 0.5) * 2 * SHADE.hue_jitter_deg;
    const lightness = band.min_l + hash01(`L:${key}`) * (band.max_l - band.min_l);

    return { h: (seed.h + jitter + 360) % 360, s: seed.s, l: lightness };
}

function too_close(a: Hsl, b: Hsl): boolean {
    const hue_delta = Math.min(Math.abs(a.h - b.h), 360 - Math.abs(a.h - b.h));
    return Math.abs(a.l - b.l) < SHADE.min_delta_l && hue_delta < SHADE.min_delta_h;
}

/**
 * Assign one colour per model. Ids are visited in sorted order so the result
 * is reproducible, and only genuinely colliding ids get salted.
 */
function build_palette(models: { id: string; lab: string }[]): Map<string, string> {
    const by_lab = new Map<string, string[]>();
    for (const { id, lab } of models) {
        const ids = by_lab.get(lab) ?? [];
        if (!ids.includes(id)) {
            ids.push(id);
        }
        by_lab.set(lab, ids);
    }

    const palette = new Map<string, string>();
    for (const [lab, ids] of by_lab) {
        const taken: Hsl[] = [];
        for (const id of [...ids].sort()) {
            let shade = shade_for(id, lab, 0);
            for (let salt = 1; salt <= 12 && taken.some((other) => too_close(other, shade)); salt++) {
                shade = shade_for(id, lab, salt);
            }
            taken.push(shade);
            palette.set(id, `hsl(${shade.h.toFixed(1)} ${shade.s.toFixed(1)}% ${shade.l.toFixed(1)}%)`);
        }
    }

    return palette;
}

/**
 * Map raw rows to {model, display, effort, score (percent), cost (USD)}.
 * pass_at_1 ships as a 0-1 fraction; rescale defensively in case that changes.
 */
function normalize(raw_rows: RawRow[]): Row[] {
    const rows = raw_rows
        .filter((row) => row[FIELDS.model] != null && row[FIELDS.score] != null && row[FIELDS.cost] != null)
        .map((row) => ({
            model: String(row[FIELDS.model]),
            display: display_name(String(row[FIELDS.model])),
            lab: lab_for(row),
            effort: String(row[FIELDS.effort] ?? "default"),
            score: Number(row[FIELDS.score]),
            cost: Number(row[FIELDS.cost]),
        }));

    if (rows.every((row) => row.score <= 1)) {
        for (const row of rows) {
            row.score *= 100;
        }
    }

    return rows;
}

/** One dataset per model: its effort configs, ordered most to least expensive. */
function to_datasets(rows: Row[]): ChartDataset[] {
    const by_model = new Map<string, Row[]>();
    for (const row of rows) {
        const points = by_model.get(row.model) ?? [];
        points.push(row);
        by_model.set(row.model, points);
    }

    const palette = build_palette([...by_model].map(([id, points]) => ({ id, lab: points[0].lab })));

    return [...by_model].map(([model_id, points]) => {
        const color = palette.get(model_id) ?? "#888888";
        return {
            label: display_name(model_id),
            data: points
                .sort((a, b) => b.cost - a.cost)
                .map((point) => ({ x: point.cost, y: point.score, effort: point.effort, model: point.display })),
            borderColor: color,
            backgroundColor: color,
            borderWidth: 1.5,
            pointRadius: 3.5,
            pointHoverRadius: 7,
            showLine: true,
            tension: 0,
        };
    });
}

/* ------------------------------------------------------------------ *
 * Chart plugins
 * ------------------------------------------------------------------ */

function css(name: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

interface Label {
    text: string;
    effort: string;
    color: string;
    x: number;
    y: number;
}

/** The label anchor of one line: its highest-scoring config inside the cutoff. */
function label_for(chart: ChartInstance, dataset: ChartDataset, index: number): Label | null {
    const meta = chart.getDatasetMeta(index);
    if (meta.hidden || !meta.data.length) {
        return null;
    }

    const x_max = chart.scales.x.max;
    const y_min = chart.scales.y.min;
    let best = -1;
    dataset.data.forEach((point, position) => {
        // a point under the floor is off-scale; anchoring a label to it would
        // park the label below the plot
        if (point.x <= x_max && point.y >= y_min && (best < 0 || point.y > dataset.data[best].y)) {
            best = position;
        }
    });

    if (best < 0 || !meta.data[best]) {
        return null;
    }

    return {
        text: dataset.label,
        effort: dataset.data[best].effort,
        color: dataset.borderColor,
        x: meta.data[best].x,
        y: meta.data[best].y - 14,
    };
}

/**
 * Direct labels. Each line is labelled at its highest-scoring config that is
 * still inside the current cost cutoff, then nudged down to avoid overlaps.
 */
const direct_labels = {
    id: "directLabels",
    afterDatasetsDraw(chart: ChartInstance): void {
        const ctx = chart.ctx;
        const area = chart.chartArea;

        const placed = chart.data.datasets
            .map((dataset, index) => label_for(chart, dataset, index))
            .filter((label): label is Label => label !== null)
            .sort((a, b) => a.y - b.y);

        for (let index = 1; index < placed.length; index++) {
            const overlaps =
                placed[index].y - placed[index - 1].y < 20 && Math.abs(placed[index].x - placed[index - 1].x) < 95;
            if (overlaps) {
                placed[index].y = placed[index - 1].y + 20;
            }
        }

        ctx.save();
        ctx.textAlign = "center";
        for (const label of placed) {
            ctx.font = "500 12px ui-sans-serif, -apple-system, sans-serif";
            const half = ctx.measureText(label.text).width / 2;
            const x = Math.min(Math.max(label.x, area.left + half), area.right - half);
            ctx.fillStyle = label.color;
            ctx.fillText(label.text, x, label.y);
            ctx.font = "10px ui-sans-serif, -apple-system, sans-serif";
            ctx.fillStyle = css("--ink-faint");
            ctx.fillText(label.effort.toUpperCase(), x, label.y + 11);
        }
        ctx.restore();
    },
};

/** Corner note marking the direction of improvement on a reversed cost axis. */
const efficiency_note = {
    id: "efficiencyNote",
    afterDraw(chart: ChartInstance): void {
        const ctx = chart.ctx;
        ctx.save();
        ctx.textAlign = "right";
        ctx.font = "italic 12px ui-sans-serif, -apple-system, sans-serif";
        ctx.fillStyle = css("--ink-faint");
        ctx.fillText("most efficient ↗", chart.chartArea.right - 6, chart.chartArea.top + 16);
        ctx.restore();
    },
};

/* ------------------------------------------------------------------ *
 * Rendering
 * ------------------------------------------------------------------ */

let chart: ChartInstance | null = null;
let datasets: ChartDataset[] = [];
let rows_cache: Row[] | null = null;

function chart_options(cutoff: number, floor: number): unknown {
    return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        layout: { padding: { top: 22, right: 10 } },
        interaction: { mode: "nearest", intersect: true },
        plugins: {
            legend: { display: false },
            tooltip: {
                displayColors: false,
                callbacks: {
                    label: (item: { raw: ChartPoint }) =>
                        `${item.raw.model} [${item.raw.effort}] · ` +
                        `${item.raw.y.toFixed(1)}% · $${item.raw.x.toFixed(2)}`,
                },
            },
        },
        scales: {
            x: {
                type: "linear",
                reverse: true, // cheap on the right, matching the published chart
                min: 0,
                max: cutoff,
                title: { display: true, text: "Avg cost per task", color: css("--ink-soft"), font: { size: 12 } },
                grid: { color: css("--grid"), drawTicks: false },
                border: { color: css("--border") },
                ticks: {
                    stepSize: cutoff <= 10 ? 2 : 5,
                    color: css("--ink-faint"),
                    font: { size: 11 },
                    padding: 6,
                    callback: (value: number) => `$${value}`,
                },
            },
            y: {
                min: floor,
                max: AXIS.score_max,
                grid: { color: css("--grid"), drawTicks: false },
                border: { color: css("--border") },
                ticks: {
                    // a raised floor leaves too little range for ten-point steps,
                    // and an odd floor like 45 would strand a lone tick at the bottom
                    stepSize: AXIS.score_max - floor <= 40 ? 5 : AXIS.score_step,
                    color: css("--ink-faint"),
                    font: { size: 11 },
                    padding: 6,
                    callback: (value: number) => `${value}%`,
                },
            },
        },
    };
}

/**
 * Apply the score floor.
 *
 * Two things at once, and both are the point: the y axis starts at the floor,
 * so what is left spreads over the full height instead of huddling in the
 * middle band, and a model whose best configuration never reaches the floor
 * drops out entirely rather than competing for label room.
 */
function apply_floor(floor: number): void {
    let dropped = 0;

    for (const dataset of datasets) {
        const best = Math.max(...dataset.data.map((point) => point.y));
        dataset.hidden = best < floor;
        dropped += dataset.hidden ? 1 : 0;
    }

    const note = el("floor-note");
    note.hidden = dropped === 0;
    note.textContent = `${dropped} ${dropped === 1 ? "model scores" : "models score"} below ${floor}% and are left out`;
}

function render(cutoff: number, floor: number): void {
    el("cutoff-out").textContent = `$${cutoff}`;
    el("floor-out").textContent = `${floor}%`;
    apply_floor(floor);

    if (chart) {
        chart.options = chart_options(cutoff, floor);
        chart.update();
        return;
    }

    chart = new Chart(el<HTMLCanvasElement>("chart"), {
        type: "scatter",
        data: { datasets },
        options: chart_options(cutoff, floor),
        plugins: [direct_labels, efficiency_note],
    });
}

function number_param(params: URLSearchParams, key: string, fallback: number): number {
    const value = Number(params.get(key));
    return params.has(key) && Number.isFinite(value) ? value : fallback;
}

function load_view(): View {
    const params = new URLSearchParams(window.location.search);

    return {
        cutoff: number_param(params, "cost", AXIS.default_cutoff),
        floor: number_param(params, "floor", AXIS.default_floor),
    };
}

function save_view(view: View): void {
    const params = new URLSearchParams();
    if (view.cutoff !== AXIS.default_cutoff) {
        params.set("cost", String(view.cutoff));
    }
    if (view.floor !== AXIS.default_floor) {
        params.set("floor", String(view.floor));
    }

    // replaceState, not pushState: the back button should leave the page rather
    // than walk back through every step of a dragged slider
    const query = params.toString();
    window.history.replaceState(null, "", query ? `?${query}` : window.location.pathname);
}

function render_meta(payload: Record<string, unknown>, rows: Row[]): void {
    const generated = payload.generated_at ?? payload.updated_at;
    const tasks = payload.n_tasks ?? payload.task_count;

    el("meta").textContent = [
        `${new Set(rows.map((row) => row.model)).size} models · ${rows.length} configs`,
        tasks ? `${tasks} tasks` : null,
        generated ? `generated ${String(generated).slice(0, 10)}` : null,
    ]
        .filter(Boolean)
        .join(" · ");
}

function show_chart(payload: unknown): void {
    const rows = normalize(extract_rows(payload));
    if (!rows.length) {
        throw new Error("Payload parsed, but no usable rows.");
    }

    rows_cache = rows;
    datasets = to_datasets(rows);

    const view = load_view();
    const max_cost = Math.ceil(Math.max(...rows.map((row) => row.cost)));

    const cost = el<HTMLInputElement>("cutoff");
    cost.max = String(max_cost);
    cost.value = String(Math.min(view.cutoff, max_cost));

    const floor = el<HTMLInputElement>("floor");
    floor.value = String(view.floor);

    render_meta((payload ?? {}) as Record<string, unknown>, rows);

    el("status").hidden = true;
    el("chart").hidden = false;
    el("controls").hidden = false;

    function draw(): void {
        const next: View = { cutoff: Number(cost.value), floor: Number(floor.value) };
        save_view(next);
        render(next.cutoff, next.floor);
    }

    cost.addEventListener("input", draw);
    floor.addEventListener("input", draw);

    matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
        if (!chart || !rows_cache) {
            return;
        }

        datasets = to_datasets(rows_cache);
        chart.data.datasets = datasets;
        draw();
        chart.update();
    });

    draw();
}

/** Opened from the filesystem, the fetch will fail CORS. Offer a manual path. */
function show_load_error(err: unknown): void {
    const template = el<HTMLTemplateElement>("load-error-template");
    const node = template.content.cloneNode(true) as DocumentFragment;

    const reason = node.querySelector(".load-error-reason");
    const source = node.querySelector(".load-error-source");
    const button = node.querySelector("button");
    if (!reason || !source || !button) {
        throw new Error("#load-error-template is incomplete.");
    }

    reason.textContent = `Could not load the leaderboard: ${message_of(err)}`;
    source.textContent = CONFIG.data_url;

    const status = el("status");
    status.replaceChildren(node);

    button.addEventListener("click", () => {
        try {
            show_chart(JSON.parse(el<HTMLTextAreaElement>("paste").value));
        } catch (parse_err) {
            alert(`That is not valid leaderboard JSON: ${message_of(parse_err)}`);
        }
    });
}

async function init(): Promise<void> {
    try {
        const res = await fetch(CONFIG.data_url, { mode: "cors" });
        if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
        }
        show_chart(await res.json());
    } catch (err) {
        show_load_error(err);
    }
}

void init();

export {};
